import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const RECAP_COLORS = ["#0099CC", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#EC4899"];

export function RecapTab({ eventId, headers }) {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchRecap = async () => {
      try {
        const res = await axios.get(`${API}/api/event/events/${eventId}/recap`, { headers });
        setRecap(res.data);
      } catch {
        toast.error("Erreur chargement recapitulatif");
      } finally {
        setLoading(false);
      }
    };
    fetchRecap();
  }, [eventId]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/event/events/${eventId}/recap/pdf`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `recap_${eventId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF telecharge");
    } catch {
      toast.error("Erreur telechargement PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0099CC]" /></div>;
  if (!recap) return <div className="text-center py-12 text-[#64748b]">Aucune donnee disponible</div>;

  const { stats, ticket_breakdown, checkin_hours, registration_timeline } = recap;

  return (
    <div className="space-y-6" data-testid="recap-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white">Recapitulatif de l'evenement</h3>
          <p className="text-xs text-[#64748b] mt-1">Statistiques cles et vue d'ensemble</p>
        </div>
        <Button onClick={handleDownloadPDF} disabled={downloading} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="download-recap-pdf-btn">
          {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          {downloading ? "Generation..." : "Exporter en PDF"}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: "Total inscrits", value: stats.total_registered, color: "#0099CC" },
          { label: "Presents", value: stats.checked_in, color: "#10B981" },
          { label: "Taux de presence", value: `${stats.attendance_rate}%`, color: "#F59E0B" },
          { label: "En attente", value: stats.waitlisted, color: "#8B5CF6" },
          { label: "Annulations", value: stats.cancelled, color: "#EF4444" },
        ].map((kpi, i) => (
          <div key={i} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{kpi.value}</p>
            <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: kpi.color }}>{kpi.label}</p>
          </div>
        ))}
      </div>

      {stats.capacity_fill_rate != null && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#64748b]">Taux de remplissage</span>
            <span className="text-sm font-bold text-white">{stats.capacity_fill_rate}%</span>
          </div>
          <div className="h-3 bg-[#1E293B] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{
              width: `${Math.min(stats.capacity_fill_rate, 100)}%`,
              background: stats.capacity_fill_rate > 90 ? "#EF4444" : stats.capacity_fill_rate > 70 ? "#F59E0B" : "#10B981",
            }} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {ticket_breakdown.length > 0 && (
          <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
            <h4 className="text-sm font-semibold text-white mb-4">Repartition par type de billet</h4>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={ticket_breakdown} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={70} label={({ type, count }) => `${type}: ${count}`}>
                  {ticket_breakdown.map((_, i) => <Cell key={i} fill={RECAP_COLORS[i % RECAP_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid #1E293B", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {checkin_hours.length > 0 && (
          <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
            <h4 className="text-sm font-semibold text-white mb-4">Pics d'arrivee (check-in par heure)</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={checkin_hours}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="hour" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid #1E293B", borderRadius: 8 }} />
                <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} name="Check-ins" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {registration_timeline.length > 0 && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
          <h4 className="text-sm font-semibold text-white mb-4">Courbe des inscriptions</h4>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={registration_timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid #1E293B", borderRadius: 8 }} />
              <Area type="monotone" dataKey="count" stroke="#0099CC" fill="#0099CC" fillOpacity={0.15} name="Inscriptions" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
